// noinspection JSUnusedGlobalSymbols
import { SelectOption } from '@/types/components/Select';

export enum UserPermission {
  Cyber = 'cyber:read',
  QuotesRead = 'quotes:read',
  QuotesWrite = 'quotes:write',
  ProgramWrite = 'programs:write',
  AnalyticsWrite = 'analytics:write', // Permission to write Tableau dashboards, Analytics documents, and Volatility models
  ClaimsRead = 'claims:read', // Permission to view claims data
  FeesRead = 'fees:read', // Permission to view fees & value-added services in policy tables
  BenefitsRead = 'benefits:read', // Permission to view misc. benefits in policy tables,
  NotificationsRead = 'notifications:read', // Permission to view notifications
  AnalyticsRead = 'analytics:read', // Permission to view analytics tab on account page
  DocumentsRead = 'documents:read', // Permission to view documents tab on account page
  DocumentsWrite = 'documents:write', // Permission to upload documents tab on account page
  OverviewRead = 'overview:read', // Permission to view overview tab on account page (individual and parent)
  PoliciesRead = 'policies:read', // Permission to view policies tab on account page (individual and parent)
  ProgramsRead = 'programs:read', // Permission to view programs tab on account page
  TowersRead = 'towers:read', // Permission to view towers tab on account page
  RenewalRead = 'renewal:read', // Permission to view renewals tab on account page
  CardviewRead = 'cardview:read', // Permission to view card view tab on parent account page
  AIVerificationWrite = 'aiverification:write', // Permission for brokers to review ai data extraction results
}

export const AccountTabPermissionOrder = [
  { permission: UserPermission.OverviewRead, tab: 'overview' },
  { permission: UserPermission.PoliciesRead, tab: 'policy-detail' },
  { permission: UserPermission.ProgramsRead, tab: 'programs' },
  { permission: UserPermission.ClaimsRead, tab: 'claim-detail' },
  { permission: UserPermission.DocumentsRead, tab: 'documents' },
  { permission: UserPermission.AnalyticsRead, tab: 'analytics' }
]

export const UserPermissionDescriptions = {
  [UserPermission.Cyber]: 'Access to the Cybeta tab and cyber benchmarking',
  [UserPermission.QuotesRead]: 'Quotes Read',
  [UserPermission.QuotesWrite]: 'Quotes Write',
  [UserPermission.ProgramWrite]: 'Program Write',
  [UserPermission.AnalyticsWrite]: 'Allow brokers to upload or edit resources (Tableau Dashboards, Analytics Documents, Volatility Models) in the Analytics hub',
  [UserPermission.ClaimsRead]: 'Allow clients and external users to view claims tab',
  [UserPermission.FeesRead]: 'Allow clients and external users to view fees and value-added services in policy tables',
  [UserPermission.BenefitsRead]: 'Allow clients and external users to view misc. benefits, life & disability, medical, dental, and vision in policy tables',
  [UserPermission.NotificationsRead]: 'Allow users to view notifications',
  [UserPermission.AnalyticsRead]: 'Allow users to view analytics tab on account page',
  [UserPermission.DocumentsRead]: 'Allow users to view documents tab on account page',
  [UserPermission.DocumentsWrite]: 'Allow users to modify documents tab on account page',
  [UserPermission.OverviewRead]: 'Allow users to view overview tab on account page',
  [UserPermission.PoliciesRead]: 'Allow users to view policies tab on account page',
  [UserPermission.ProgramsRead]: 'Allow users to view programs tab on account page',
  [UserPermission.TowersRead]: 'Allow users to view towers tab on account page',
  [UserPermission.RenewalRead]: 'Allow users to view renewals tab on account page',
  [UserPermission.CardviewRead]: 'Allow users to view card view tab on parent account page',
  [UserPermission.AIVerificationWrite]: 'Allow brokers to access the AI Data Extraction Verification Results page',
};

// list of permissions that brokers need to have explicitly assigned for certain access
export const BrokerRequiredPermissions = new Set([
  UserPermission.Cyber,
  UserPermission.NotificationsRead,
  UserPermission.AIVerificationWrite,
]);

export const AWSUserStatus = {
  FORCE_CHANGE_PASSWORD: 'FORCE_CHANGE_PASSWORD'
} as const;

export interface UserPermissionDetails {
  userId: number;
  name?: string;
  userEmail: string;
  userSubId: string;
  userName?: string;
  userTypeId: number;
  userTypeName?: string;
  deletedAt?: Date | null;
  deletedBy?: number | null;
  parent?: boolean;
  activeDate?: Date;
  createdDate?: Date;
  userStatus: string;
  permissions?: string[];
  accounts?: AccountDetails[];
  carrierIds?: number[];
  wholesalerIds?: number[];
}

export interface AccountDetails {
  accountId: number;
  accountName: string;
  parent: boolean;
}

export interface UserPermissionDetailsWithRoles extends UserPermissionDetails {
  roles: string[];
  isAdmin: boolean;
}

export interface UserRoles {
  userSubId: string;
  roles: string[];
}

export interface AccountSelectOption extends SelectOption {
  isParent: boolean;
}

export interface AppRole {
  id: number,
  name: string,
  description?: string,
}

export interface AllowedPermissionsByRole {
  roleName: string;
  permissions: string[];
}
